/**
 * 이 파일은 Aui 의 일부입니다. (https://aui.moimz.tools)
 *
 * TextArea 필드 클래스를 정의한다.
 *
 * @file /src/components/form/field/textarea/TextArea.ts
 * @author sungjin <esung246@naddle.net>
 * @license MIT License
 * @modified 2026. 9. 17.
 */
import * as parent from '@components/form/field/Field';
import { Dom } from '@utils/Dom';
import { Html } from '@utils/Html';
import { Language, LanguageText } from '@utils/Language';
import './TextArea.css';

export interface Listeners<T> extends parent.Listeners<T> {}

export interface Properties<T> extends parent.Properties<T> {
    /**
     * 필드값이 없을 경우 보일 placeHolder
     */
    placeholder?: LanguageText;

    /**
     * textarea 의 라인수
     */
    rows?: number;

    /**
     * textarea 높이를 자동으로 조절할지 여부
     */
    autoHeight?: boolean;

    /**
     * value 없을 경우 반환값(없으면 null)
     */
    defaultValue?: string;

    /**
     * 이벤트리스너
     */
    listeners?: Listeners<T>;
}

export class TextArea extends parent.Field {
    /**
     * 컴포넌트 확장타입
     */
    variant = 'textarea';

    /**
     * 필드값이 없을 경우 보일 placeHolder
     */
    placeholder: LanguageText;

    /**
     * textarea 의 라인수
     */
    rows: number | null = null;

    /**
     * textarea 높이를 자동으로 조절할지 여부
     */
    autoHeight: boolean | null = null;

    /**
     * value 없을 경우 반환값(없으면 null)
     */
    defaultValue: string | null = null;

    /**
     * textarea value length
     */
    latestLength: number = 0;

    /**
     * textarea value 의 줄바꿈(\n) 갯수
     */
    latestLineCount: number = 0;

    /**
     * INPUT 필드 DOM
     */
    $input: Dom<HTMLTextAreaElement> = null;

    /**
     * Textarea 필드 클래스를 정의한다.
     *
     * @param properties - 초기설정
     */
    constructor(public properties: Properties<TextArea> = null) {
        super(properties);

        this.placeholder = this.properties.placeholder ?? null;
        this.rows = this.properties.rows ?? 5;
        this.autoHeight = this.properties.autoHeight ?? true;
        this.defaultValue = this.properties.defaultValue ?? null;
    }

    /**
     * Textarea 필드 DOM 을 가져온다.
     *
     * @returns 텍스트에리어 DOM
     */
    $getInput(): Dom<HTMLTextAreaElement> {
        if (this.$input === null) {
            this.$input = Html.create('textarea', {
                name: this.name ?? this.id,
                rows: this.rows.toString(),
            });

            if (this.readonly == true) {
                this.$input.setAttr('readonly', 'readonly');
            }

            this.$input.on('input', async (me) => {
                let value = me.getValue();
                if (value.length == 0) {
                    value = null;
                }

                if (this.autoHeight == true) {
                    this.updateHeight();
                }

                await this.setValue(value, false, false);
                const validation = this.getValidationHistory(value, true);
                this.updateValidation(validation);
            });

            this.$input.on('blur', async (me) => {
                let value = me.getValue();
                if (value.length == 0) {
                    value = null;
                }
                await this.setValue(value, false, false);
                await this.isValid(value);
            });
        }

        return this.$input;
    }

    /**
     * Textarea 필드의 높이를 콘텐츠에 따라 갱신한다.
     * autoHeight 가 true 인 경우, Textarea 의 값이 변경될때마다 호출된다.
     */
    updateHeight(): void {
        const value = this.$getInput().getValue() ?? '';
        const newlineCount = (value.match(/\n/g) ?? []).length;

        /**
         * autoHeight 가 true 이든 false 이든 최초 높이를 계산한다.
         * 이 최초 높이는 autoHeight 가 true 일때는 최소높이로 사용한다.
         * 최초높이는 this.rows 의 설정값에 따른 높이, 그리고 placeHolder 에도 줄바꿈이 될 수 있으니, placeHolder 의 라인수 중 큰것을 기준으로 한다.
         */
        const lineHeight = parseFloat(this.$getInput().getStyle('line-height') || '0');
        const paddingTop = parseFloat(this.$getInput().getStyle('padding-top') || '0');
        const paddingBottom = parseFloat(this.$getInput().getStyle('padding-bottom') || '0');
        const placeholder = this.$getInput().getEl().placeholder ?? '';
        const placeholderLines = placeholder.length > 0 ? placeholder.split('\n').length : 1;
        const lines = Math.max(this.rows, placeholderLines);
        const minHeight = Math.ceil(lineHeight * lines + paddingTop + paddingBottom + 2);

        /**
         * autoHeight 가 true 인 경우, 값의 라인수에 따라 높이를 계산한다.
         * 기존에 1px 로 높이를 강제로 줄인후 계산했지만, minHeight 로 줄여두어도 된다.
         * minHeight 과 값에 따른 높이 계산 중 큰 값으로 높이를 적용한다.
         *
         * latestLength 보다 현재값 길이가 길거나, latestLineCount 보다 현재 \n 갯수가 많은 경우에만
         * 높이 계산을 새로 한다. (길이만으로 판단하면, 길이는 줄었지만 줄바꿈은 늘어난 경우를 놓친다)
         */
        if (this.autoHeight == true) {
            this.$getInput().setStyle('height', minHeight + 'px');

            if (value.length > this.latestLength || newlineCount > this.latestLineCount) {
                const height = Math.max(minHeight, this.$getInput().getScrollHeight(true));
                this.$getInput().setStyle('height', height + 'px');
            }
        } else {
            this.$getInput().setStyle('height', minHeight + 'px');
        }

        this.latestLength = value.length;
        this.latestLineCount = newlineCount;
    }

    /**
     * 필드에 포커스를 지정한다.
     */
    focus(): void {
        this.$getInput().focus();
    }

    /**
     * 필드값을 지정한다.
     *
     * @param value - 값
     * @param is_init - 초기값여부
     * @param is_update - INPUT 태그의 값을 변경할 지 여부
     */
    async setValue(value: any, is_init: boolean = false, is_update: boolean = true): Promise<void> {
        await this.lockValue();

        if (is_init == true || is_update == true) {
            this.$getInput().setValue(value?.toString() ?? '');

            if (this.autoHeight == true) {
                this.updateHeight();
            }
        }

        await this.releaseValue(value, is_init);
    }

    /**
     * 읽기전용상태를 변경한다.
     *
     * @param readonly - 읽기전용여부
     */
    setReadonly(readonly: boolean): void {
        if (readonly == true) {
            this.$getInput().setAttr('readonly', 'readonly');
        } else {
            this.$getInput().removeAttr('readonly');
        }

        super.setReadonly(readonly);
    }

    /**
     * 컴포넌트의 비활성화여부를 설정한다.
     *
     * @param disabled - 비활성화여부
     * @returns 컴포넌트
     */
    setDisabled(disabled: boolean): this {
        this.$getInput().setDisabled(disabled);
        return super.setDisabled(disabled);
    }

    /**
     * 필드영역을 렌더링한다.
     */
    async renderField(): Promise<void> {
        if (this.placeholder !== null) {
            const placeholder = await Language.resolveText(this.placeholder);
            this.$getInput().getEl().placeholder = placeholder.replace(/(<br\s*\/?>|\r\n|\r|\n)/gi, '\n');
        }
        if (this.autoHeight !== true) {
            this.$getInput().setStyle('overflow', 'auto');
        }
        this.$getContent().append(this.$getInput());
    }

    /**
     * 컴포넌트 렌더링이 완료된 이후 초기상태정의가 필요할 경우, 상태를 렌더링한다.
     */
    async renderState(): Promise<void> {
        if (this.initValue !== null) {
            await this.setValue(this.initValue, true);
        }

        this.updateHeight();
    }
}
